/**
 * VerifyPage - landing apres clic sur le lien email.
 * URL : /auth/verify?token=xxx
 *
 * Flow :
 *  1. Extract token from query string
 *  2. POST /api/auth/magic-link/verify
 *  3. Si OK : set cookie + redirect /
 *  4. Si KO : message d erreur + bouton "Retourner a la connexion"
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Body1, Button, Spinner, Title2, tokens } from '@fluentui/react-components';
import { useAuth } from '../auth/AuthContext';
import { BrandLogo } from '../components/BrandLogo';

type State = 'verifying' | 'ok' | 'error';

export function VerifyPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyMagicLink } = useAuth();
  const [state, setState] = useState<State>('verifying');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      setError(t('auth.verify.expired'));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await verifyMagicLink(token);
        if (!cancelled) {
          setState('ok');
          // Petit delai pour que le user voit le succes avant le redirect
          setTimeout(() => navigate('/', { replace: true }), 800);
        }
      } catch (e) {
        if (!cancelled) {
          setState('error');
          setError((e as Error)?.message ?? t('auth.verify.expired'));
        }
      }
    })();
    return () => { cancelled = true; };
    // verifyMagicLink est stable (useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <div
      style={{
        minHeight:      '100vh',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        background:     `linear-gradient(135deg, ${tokens.colorNeutralBackground2} 0%, ${tokens.colorNeutralBackground3} 100%)`,
        padding:        '24px',
      }}
    >
      <div
        style={{
          width:        '100%',
          maxWidth:     420,
          padding:      '48px 32px',
          background:   tokens.colorNeutralBackground1,
          borderRadius: 12,
          boxShadow:    '0 8px 32px rgba(0,0,0,0.08)',
          border:       `1px solid ${tokens.colorNeutralStroke2}`,
          textAlign:    'center',
        }}
      >
        <BrandLogo height={56} />

        {state === 'verifying' && (
          <>
            <Title2 as="h1" style={{ marginTop: 24 }}>
              {t('auth.verify.checking')}
            </Title2>
            <div style={{ marginTop: 24 }}>
              <Spinner size="large" />
            </div>
          </>
        )}

        {state === 'ok' && (
          <>
            <Title2 as="h1" style={{ marginTop: 24, color: tokens.colorPaletteGreenForeground1 }}>
              ✓ {t('auth.verify.success')}
            </Title2>
          </>
        )}

        {state === 'error' && (
          <>
            <Title2 as="h1" style={{ marginTop: 24, color: tokens.colorPaletteRedForeground1 }}>
              ✕ {t('auth.verify.expired')}
            </Title2>
            <Body1 block style={{ marginTop: 12 }}>
              {error}
            </Body1>
            <Body1 block style={{ marginTop: 8, color: tokens.colorNeutralForeground3, fontSize: 13 }}>
              {t('auth.verify.expiredBody')}
            </Body1>
            <Button as="a" appearance="primary" style={{ marginTop: 24 }}>
              <Link to="/login" style={{ color: 'inherit', textDecoration: 'none' }}>
                {t('auth.verify.backToLogin')}
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
